import { convertAmountToAud } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';

import { FixedCostPriceItem, SlidingScalePricingItem } from '~/types/tours/costPrice';

export interface SupplierCostPrices {
  serviceId: string;
  sliding: Array<SlidingScalePricingItem>;
  fixed: Array<FixedCostPriceItem>;
}

export type AggregateCostPrices =
  | { kind: 'sliding'; pricing: Array<SlidingScalePricingItem> }
  | { kind: 'fixed'; pricing: Array<FixedCostPriceItem> };

interface RawAmount {
  amount: number;
  currency: string;
}

interface SourceScaleRow {
  season: string;
  tourConfiguration: string;
  packageConfiguration: string;
  scale: Array<RawAmount>;
  singleSupplement: RawAmount;
}

interface SourceFixedRow {
  season: string;
  tourConfiguration: string;
  packageConfiguration: string;
  price: RawAmount;
}

const rowKey = (row: { season: string; tourConfiguration: string; packageConfiguration: string }): string =>
  `${row.season.toLowerCase()}-${row.tourConfiguration.toLowerCase()}-${row.packageConfiguration.toLowerCase()}`;

const rawAmount = (amount: number, currency: string): RawAmount => ({ amount, currency });

const scaleRows = (sources: Array<SupplierCostPrices>): Array<SourceScaleRow> => {
  const longestScale = Math.max(1, ...sources.flatMap((source) => source.sliding.map((price) => price.scale.length)));

  return sources.flatMap((source) => [
    ...source.sliding.map((price, rowIndex) => ({
      season: price.season,
      tourConfiguration: price.tourConfiguration,
      packageConfiguration: price.packageConfiguration,
      scale: price.scale.map((amount) => rawAmount(amount, price.currency)),
      singleSupplement: rawAmount(price.singleSupplement, price.currency),
    })),
    ...source.fixed.map((price) => {
      const priceAmount = rawAmount(price.price, price.currency);

      return {
        season: price.season,
        tourConfiguration: price.tourConfiguration,
        packageConfiguration: price.packageConfiguration,
        scale: Array.from({ length: longestScale }, () => priceAmount),
        singleSupplement: rawAmount(0, price.currency),
      };
    }),
  ]);
};

const fixedRows = (sources: Array<SupplierCostPrices>): Array<SourceFixedRow> =>
  sources.flatMap((source) =>
    source.fixed.map((price) => ({
      season: price.season,
      tourConfiguration: price.tourConfiguration,
      packageConfiguration: price.packageConfiguration,
      price: rawAmount(price.price, price.currency),
    })),
  );

/** The distinct source currencies that require one cached AUD base rate each. */
export function getAggregateCostPriceCurrencies(sources: Array<SupplierCostPrices>): Array<string> {
  const currencies = sources.flatMap((source) => [
    ...source.sliding.map((price) => price.currency),
    ...source.fixed.map((price) => price.currency),
  ]);

  return Array.from(new Set(currencies));
}

const converted = (amount: RawAmount, ratesByCurrency: Record<string, number | undefined>): number | undefined =>
  convertAmountToAud(amount.amount, amount.currency, ratesByCurrency);

export function aggregateCostPricesInAud(
  sources: Array<SupplierCostPrices>,
  ratesByCurrency: Record<string, number | undefined>,
): AggregateCostPrices | undefined {
  const hasSourcePrices = sources.some((source) => source.sliding.length > 0 || source.fixed.length > 0);
  const currencies = getAggregateCostPriceCurrencies(sources);

  if (!hasSourcePrices || currencies.some((currency) => ratesByCurrency[currency] === undefined)) {
    return undefined;
  }

  const hasSlidingScale = sources.some((source) => source.sliding.length > 0);

  if (hasSlidingScale) {
    const grouped = new Map<string, Array<SourceScaleRow>>();
    scaleRows(sources).forEach((row) => {
      const key = rowKey(row);
      grouped.set(key, [...(grouped.get(key) ?? []), row]);
    });

    const pricing = Array.from(grouped.values()).flatMap((rows) => {
      const first = rows[0];
      const length = Math.max(...rows.map((row) => row.scale.length));
      const scale = Array.from({ length }, (_value, index) => {
        const values = rows.map((row) => converted(row.scale[Math.min(index, row.scale.length - 1)], ratesByCurrency));
        return values.some((value) => value === undefined)
          ? undefined
          : values.reduce((total, value) => total + value!, 0);
      });
      const supplements = rows.map((row) => converted(row.singleSupplement, ratesByCurrency));

      if (scale.some((value) => value === undefined) || supplements.some((value) => value === undefined)) {
        return [];
      }

      return [
        {
          season: first.season,
          tourConfiguration: first.tourConfiguration,
          packageConfiguration: first.packageConfiguration,
          currency: 'AUD',
          scale: scale as Array<number>,
          singleSupplement: supplements.reduce((total, value) => total + value!, 0),
        },
      ];
    });

    return { kind: 'sliding', pricing };
  }

  const grouped = new Map<string, Array<SourceFixedRow>>();
  fixedRows(sources).forEach((row) => {
    const key = rowKey(row);
    grouped.set(key, [...(grouped.get(key) ?? []), row]);
  });
  const pricing = Array.from(grouped.values()).flatMap((rows) => {
    const values = rows.map((row) => converted(row.price, ratesByCurrency));

    if (values.some((value) => value === undefined)) {
      return [];
    }

    const first = rows[0];
    return [
      {
        season: first.season,
        tourConfiguration: first.tourConfiguration,
        packageConfiguration: first.packageConfiguration,
        currency: 'AUD',
        price: values.reduce((total, value) => total + value!, 0),
      },
    ];
  });

  return { kind: 'fixed', pricing };
}
